import * as Sentry from "@sentry/react";
import React, { useState } from "react";

import { faTriangleExclamation } from "@performyard/fontawesome/pro-solid-svg-icons";
import { PageBanner } from "@performyard/py-components";
import { toast } from "@performyard/py-components";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import { GridCol } from "../../../components/Grid";
import PageTitle from "../../../components/PageTitle";
import Spacer from "../../../components/Spacer";
import { SectionLayout } from "../AIAssistants/components/SectionLayout";
import { SettingsToggle } from "../AIAssistants/components/SettingsToggle";

import { useAiSettings, useUpdateAiSettings } from "../../../api/hooks";
import { getFeatureFlagQuery, useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import { companyAISettingsQuery, currentUserQuery } from "../../../api/queries";
import { dataLoader } from "../../../api/queries/dataLoader";

import ErrorPage from "../../Error";

/** Data loader function */
export const conversationalAISettingsLoader = () => {
	const queries = [companyAISettingsQuery, getFeatureFlagQuery];

	return dataLoader([[currentUserQuery], queries]);
};

const ConversationalAISettings = () => {
	// AI settings data management
	const { mutateAsync: updateAiSettings } = useUpdateAiSettings();
	const { data } = useAiSettings();

	// AI feature flags
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: conversationalAIFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-conversational-ui-enabled", false);
	const { value: conversationalAIGoalSuggestionsFeatureFlagEnabled } = useFeatureFlagQuery(
		"ff-temp-conversational-ai-goal-suggestions",
		false
	);

	// toggles
	const isAskHrEnabled = data?.conversationalAiSettings?.askHrEnabled ?? false;
	const isGoalCreationEnabled = data?.conversationalAiSettings?.goalCreationEnabled ?? false;

	// states
	const [showDocumentBanner, setShowDocumentBanner] = useState(false);

	// handlers
	const handleAskHrToggle = async (val: boolean) => {
		await updateAiSettings({ conversationalAiSettings: { ...data!.conversationalAiSettings, askHrEnabled: val } });

		// Show banner when enabling, hide when disabling
		setShowDocumentBanner(val);

		toast.success(`Ask HR ${val === true ? "Enabled" : "Disabled"}.`);
	};

	const handleGoalCreationToggle = async (val: boolean) => {
		await updateAiSettings({
			conversationalAiSettings: { ...data!.conversationalAiSettings, goalCreationEnabled: val },
		});
		toast.success(`AI-Assisted Goal Creation ${val === true ? "Enabled" : "Disabled"}.`);
	};

	const handleDismissBanner = () => {
		setShowDocumentBanner(false);
	};

	if (!aiFeatureFlagEnabled || !conversationalAIFeatureFlagEnabled) {
		return <ErrorPage code={404} />;
	}

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<Spacer height={4} />
			<AdminContainer>
				<PageTitle title="Ask PerformYard" />
				<AdminTitle>Ask PerformYard</AdminTitle>
				<Spacer height={3} />
				<SectionLayout
					key="ask-performyard-settings-section"
					title="Ask PerformYard Settings"
					description="Control whether members can access Ask PerformYard and what actions it can perform. Disabling all settings will turn off the feature completely."
				>
					<div className="col-span-5 grid grid-cols-5 gap-4">
						{/* enable ask HR toggle */}
						<SettingsToggle
							key="ask-hr-toggle"
							id="enable-ask-hr"
							label="Enable Ask HR"
							description="Disabling this setting will remove Ask PerformYard's ability to use company uploaded documents when answering questions from users."
							checked={isAskHrEnabled}
							onCheckedChange={handleAskHrToggle}
							dataTestId="ask-hr-toggle"
							labelMaxWidth="max-w-[310px]"
						/>
						{/* Document management banner */}
						{showDocumentBanner && (
							<GridCol span={5}>
								<PageBanner
									color="primary"
									icon={faTriangleExclamation}
									mainTitle="Manage Documents"
									subTitle="We've enabled a set of documents from your Company Resources to help you get started with  Ask PerformYard. You can review and manage document access at any time, or upload new additional ones to tailor the feature to your organization's needs."
									bottomActionButton={{
										children: "Go to Company Resources",
										href: "/company/resources",
										color: "primary",
										"data-testid": "go-to-company-resources-button",
									}}
									onDismiss={handleDismissBanner}
								/>
							</GridCol>
						)}
					</div>
					{/* enable ai-assisted goal creation toggle */}
					{conversationalAIGoalSuggestionsFeatureFlagEnabled && (
						<SettingsToggle
							key="ai-assisted-goal-creation-toggle"
							id="enable-ai-assisted-goal-creation"
							label="Enable AI-Assisted Goal Creation"
							description="Disabling this setting will remove Ask PerformYard's ability to create goals for users."
							checked={isGoalCreationEnabled}
							onCheckedChange={handleGoalCreationToggle}
							dataTestId="ai-assisted-goal-creation-toggle"
							labelMaxWidth="max-w-[310px]"
						/>
					)}
				</SectionLayout>
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default ConversationalAISettings;
